import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  CheckCircle2, Send, Play, Activity, MessageSquare, Ticket, Upload,
  CalendarDays, Plus, X, Loader2, Wallet, Download, FileText, AlertCircle,
  Trophy, AlertTriangle, Info,
} from 'lucide-react';
import PipelineTabs from '@/components/PipelineTabs';
import ReferencesPanel from '@/components/ReferencesPanel';
import DeadlineTicker from '@/components/DeadlineTicker';
import RevisionsInboxWidget from '@/components/RevisionsInboxWidget';
import InlineSubmitButton from '@/components/InlineSubmitButton';
import { MyTasksList } from '@/components/MyTasksRow';

// Each route maps to a content section. The top tab strip was removed
// (redundant with the sidebar — founder feedback May 9, 2026); the URL is
// now the sole driver of which section renders.
const TAB_MAP = {
  '/dashboard': 'status',
  '/em-tasks': 'tasks',
  '/em-references': 'references',
  '/em-leaves': 'salary',  // legacy redirect — leaves now live inside Salary
  '/em-salary': 'salary',
  '/em-communicate': 'communicate',
};
const LEAVE_KIND_LABELS = { casual: 'Casual', sick: 'Sick', earned: 'Earned', loss_of_pay: 'LOP' };
// Static class lookup so Tailwind JIT purges keep these
const LEAVE_CLASSES = {
  casual:       { card: 'border-cyan-500/30',    label: 'text-cyan-300',    bar: 'bg-cyan-500',    pill: 'bg-cyan-500/15 text-cyan-300' },
  sick:         { card: 'border-rose-500/30',    label: 'text-rose-300',    bar: 'bg-rose-500',    pill: 'bg-rose-500/15 text-rose-300' },
  earned:       { card: 'border-emerald-500/30', label: 'text-emerald-300', bar: 'bg-emerald-500', pill: 'bg-emerald-500/15 text-emerald-300' },
  loss_of_pay:  { card: 'border-zinc-700',       label: 'text-zinc-300',    bar: 'bg-zinc-500',    pill: 'bg-zinc-700/40 text-zinc-300' },
};

export default function EmployeeDashboard() {
  const { api, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState(TAB_MAP[location.pathname] || 'status');
  const [deptTab, setDeptTab] = useState('all');
  const [updating, setUpdating] = useState(null);

  // Leaves
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [showApply, setShowApply] = useState(false);

  // Salary
  const [salary, setSalary] = useState(null);
  const [payslips, setPayslips] = useState([]);
  // Comp-off audit log — surfaces "where did this earned leave come from?"
  const [compOffLog, setCompOffLog] = useState([]);
  // Rewards & Penalty (running monthly totals + per-task list)
  const [rewards, setRewards] = useState(null);
  const [rules, setRules] = useState(null);

  // Communicate
  const [msgText, setMsgText] = useState('');
  const [ticketText, setTicketText] = useState('');
  const [msgSent, setMsgSent] = useState(false);
  const [ticketSent, setTicketSent] = useState(false);

  const loadTasks = () => api.get('/tasks').then(r => {
    // Tasks store the readable label in either `title` (Kitsu / legacy) or
    // `name` (newer rows). Some legacy rows have neither — for those we
    // synthesise a label from parent_code + task_type so Khojendra (and any
    // other employee with an old import) doesn't see blank rows.
    const norm = (Array.isArray(r.data) ? r.data : []).map(t => {
      const fallback = t.parent_code
        ? `${t.parent_code} · ${(t.task_type || '').replace(/_/g, ' ')}`
        : (t.task_type || '').replace(/_/g, ' ') || 'Task';
      return { ...t, name: t.name || t.title || fallback };
    });
    setTasks(norm);
  }).catch(() => {});
  const loadLeaves = () => Promise.all([
    api.get('/employee/leaves/balance').then(r => {
      setBalance(r.data.balance);
      setCompOffLog(r.data.comp_off_log || []);
    }).catch(() => {}),
    api.get('/employee/leaves/requests').then(r => setLeaves(r.data.requests || [])).catch(() => {}),
  ]);
  const loadSalary = () => Promise.all([
    api.get('/employee/salary').then(r => setSalary(r.data)).catch(() => {}),
    api.get('/employee/payslips').then(r => setPayslips(r.data.payslips || [])).catch(() => {}),
  ]);
  // Rewards & Penalty engine — keeps the dashboard's running monthly figure
  // in sync with what will land on the artist's payslip at month-end.
  const loadRewards = () => Promise.all([
    api.get('/employee/rewards/me').then(r => setRewards(r.data)).catch(() => {}),
    api.get('/employee/rewards/rules').then(r => setRules(r.data)).catch(() => {}),
  ]);

  useEffect(() => { loadTasks(); loadLeaves(); loadSalary(); loadRewards(); /* eslint-disable-next-line */ }, [api]);
  useEffect(() => { setTab(TAB_MAP[location.pathname] || 'status'); }, [location.pathname]);

  const updateTask = async (taskId, status, progress) => {
    setUpdating(taskId);
    await api.put(`/tasks/${taskId}`, { status, progress });
    loadTasks(); setUpdating(null);
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    await api.post('/ai/chat', { message: `[Message to Manager] ${msgText}`, context: 'general' });
    setMsgSent(true); setMsgText('');
    setTimeout(() => setMsgSent(false), 3000);
  };
  const raiseTicket = async () => {
    if (!ticketText.trim()) return;
    await api.post('/disputes', { description: ticketText, type: 'scope', project_id: tasks[0]?.project_id || '' });
    setTicketSent(true); setTicketText('');
    setTimeout(() => setTicketSent(false), 3000);
  };

  // Merge the rewards engine's per-task outcomes so the DeadlineTicker can
  // render frozen reward/penalty badges. In-flight tasks fall back to the
  // live timer in DeadlineTicker.
  const taskOutcomes = (rewards?.items || []).reduce((acc, item) => {
    if (item?.task_id) acc[item.task_id] = item;
    return acc;
  }, {});
  const enrichedTasks = tasks.map(t => taskOutcomes[t.id] ? { ...t, outcome: taskOutcomes[t.id] } : t);
  // Pipeline-tab filter mirrors the counter logic in PipelineTabs.js: legacy
  // task types (modeling / texturing) roll into the merged ASSET BUILD tab so
  // existing data — like Khojendra's 3 modeling tasks — actually shows up
  // when the artist clicks the matching pill. (Founder bug May 9, 2026.)
  const matchesDeptTab = (t, key) => {
    if (key === 'all') return true;
    const tt = t.task_type || 'other';
    if (key === 'asset_build' && (tt === 'modeling' || tt === 'texturing')) return true;
    return tt === key;
  };
  const filtered = enrichedTasks.filter(t => matchesDeptTab(t, deptTab));
  const completed = filtered.filter(t => t.status === 'completed');
  const inProgress = filtered.filter(t => t.status === 'in_progress');
  const pending = filtered.filter(t => t.status === 'not_started');
  const review = filtered.filter(t => t.status === 'review');
  const blocked = filtered.filter(t => t.status === 'blocked');

  return (
    <div className="space-y-4" data-testid="employee-dashboard">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Welcome, {user?.full_name}</h1>
          <p className="text-xs text-zinc-400 mt-1">Employee Dashboard</p>
        </div>
        {/* "Submit for Approval" CTA removed May 24, 2026 (founder spec):
            employees submit ONLY via the inline SUBMIT button on each
            task row in MY TASKS. No separate submit surface. */}
      </div>

      {/* Sub-nav tabs */}
      {['status', 'tasks'].includes(tab) && (
        <PipelineTabs tasks={tasks} activeTab={deptTab} onTabChange={setDeptTab} showAll={true} />
      )}

      {/* ══ STATUS ══ */}
      {tab === 'status' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Total Assigned" value={filtered.length} color="text-white" />
            <Stat label="Completed" value={completed.length} color="text-emerald-400" />
            <Stat label="In Progress" value={inProgress.length} color="text-blue-400" />
            <Stat label="In Review" value={review.length} color="text-violet-400" />
            <Stat label="Blocked" value={blocked.length} color="text-rose-400" />
          </div>
          <p className="text-[10px] text-amber-400 flex items-center gap-1">
            <Activity className="w-3 h-3" /> Every status update is instantly visible to the production team
          </p>

          {/* Revisions inbox — surfaces CG Sup pin-notes so artists know what to fix. */}
          <RevisionsInboxWidget />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Column title={`In Progress (${inProgress.length})`} accent="border-blue-500/20">
              {inProgress.map(t => (
                <TaskRow key={t.id} t={t} updating={updating} updateTask={updateTask} variant="active" />
              ))}
              {inProgress.length === 0 && <Empty>No tasks in progress</Empty>}
            </Column>
            <Column title={`Pending (${pending.length + blocked.length})`}>
              {pending.map(t => (
                <div key={t.id} className="px-4 py-3">
                  <p className="text-xs text-zinc-200">{t.name}</p>
                  <span className="text-[10px] text-zinc-400">{t.task_type}</span>
                  <button onClick={() => updateTask(t.id, 'in_progress', 10)}
                    className="mt-2 h-6 px-2 bg-blue-500/20 text-blue-400 text-[9px] rounded flex items-center gap-1">
                    <Play className="w-3 h-3" /> Start
                  </button>
                </div>
              ))}
              {blocked.map(t => (
                <div key={t.id} className="px-4 py-3 bg-rose-500/5">
                  <p className="text-xs text-rose-300">{t.name}</p>
                  <p className="text-[9px] text-rose-400">BLOCKED</p>
                </div>
              ))}
              {pending.length + blocked.length === 0 && <Empty>Nothing pending</Empty>}
            </Column>
            <Column title={`Done (${completed.length + review.length})`} accent="border-emerald-500/20">
              {review.map(t => (
                <div key={t.id} className="px-4 py-3 bg-violet-500/5">
                  <p className="text-xs text-zinc-200">{t.name} <span className="text-[8px] px-1.5 py-0.5 bg-violet-500/20 text-violet-300 rounded">In Review</span></p>
                </div>
              ))}
              {completed.map(t => (
                <div key={t.id} className="px-4 py-3 flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  <p className="text-xs text-zinc-300">{t.name}</p>
                </div>
              ))}
              {completed.length + review.length === 0 && <Empty>Nothing complete yet</Empty>}
            </Column>
          </div>
        </div>
      )}

      {/* ══ TASKS ══ */}
      {tab === 'tasks' && (
        <div className="space-y-2">
          {/* Founder bug Feb 2026 (vawworld.in, new employee Rajendra
              Koushal saw the OLD My Tasks UI): this page rendered its
              own legacy <div> row inline (lines 225-258) and never
              picked up the rewrite shipped to FreelancerDashboardNew /
              VendorTasksPage. We now mount the SHARED <MyTasksList>
              component so every dashboard (Employee · Freelancer ·
              Vendor · Vendor-Team) renders the exact same row —
              status lifecycle, raw-material strip with NAS paths,
              dual upload (CBA · SHOT), and lighting-brief chip all
              inherit automatically going forward. */}
          <div className="bg-amber-500/5 border border-amber-500/30 rounded-md p-3">
            <p className="text-[11px] text-amber-200 leading-snug">
              <strong className="text-amber-300">Strict naming:</strong> the canonical filename below each row is the only name your upload will accept.
              Save your file with that exact name (click to copy), then hit Submit.
              Wrong names are rejected with a clear error so you can rename and retry.
            </p>
          </div>
          <MyTasksList
            tasks={(filtered || []).map(t => ({
              ...t,
              project_name: t.project_name || t.project?.name,
              work_order_no: t.work_order_no || t.wo_no,
              stage: t.stage || t.task_type,
            }))}
            onSubmitted={() => loadTasks()}
          />
          {(filtered || []).length === 0 && (
            <p className="text-xs text-zinc-400 text-center py-8">No tasks in this department</p>
          )}
        </div>
      )}

      {/* ══ REFERENCES ══ */}
      {tab === 'references' && (
        <ReferencesPanel />
      )}

      {/* ══ SALARY & LEAVES ══ (merged per founder spec May 2026) */}
      {tab === 'salary' && (
        <SalaryAndLeavesTab
          salary={salary} payslips={payslips}
          balance={balance} leaves={leaves}
          compOffLog={compOffLog}
          rewards={rewards} rules={rules}
          onApply={() => setShowApply(true)} api={api} reload={loadLeaves}
        />
      )}

      {/* ══ COMMUNICATE ══ */}
      {tab === 'communicate' && (
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
            <h3 className="text-xs font-semibold text-zinc-100 uppercase tracking-wider mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-cyan-400" /> Message Reporting Manager
            </h3>
            <div className="flex gap-2">
              <input value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Type your message..."
                data-testid="emp-msg-input"
                className="flex-1 h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-xs text-white focus:ring-1 focus:ring-cyan-500 focus:outline-none" />
              <button onClick={sendMessage} disabled={!msgText.trim()} data-testid="emp-msg-send"
                className="h-9 px-4 bg-cyan-600 hover:bg-cyan-500 text-white text-xs rounded-md disabled:opacity-40 flex items-center gap-1">
                <Send className="w-3.5 h-3.5" /> Send
              </button>
            </div>
            {msgSent && <p className="text-[10px] text-emerald-400 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Sent</p>}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4">
            <h3 className="text-xs font-semibold text-zinc-100 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Ticket className="w-4 h-4 text-amber-400" /> Raise a Ticket
            </h3>
            <div className="flex gap-2">
              <input value={ticketText} onChange={e => setTicketText(e.target.value)} placeholder="Describe your issue..."
                data-testid="emp-ticket-input"
                className="flex-1 h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-xs text-white focus:ring-1 focus:ring-amber-500 focus:outline-none" />
              <button onClick={raiseTicket} disabled={!ticketText.trim()} data-testid="emp-ticket-send"
                className="h-9 px-4 bg-amber-600 hover:bg-amber-500 text-white text-xs rounded-md disabled:opacity-40 flex items-center gap-1">
                <Ticket className="w-3.5 h-3.5" /> Raise
              </button>
            </div>
            {ticketSent && <p className="text-[10px] text-emerald-400 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Raised</p>}
          </div>
        </div>
      )}

      {showApply && <ApplyLeaveModal api={api} onClose={() => setShowApply(false)} onCreated={() => { setShowApply(false); loadLeaves(); }} />}
    </div>
  );
}

// ──────────── Salary & Leaves tab (merged per founder spec) ────────────
function SalaryAndLeavesTab({ salary, payslips, balance, leaves, compOffLog, rewards, rules, onApply, api, reload }) {
  const [section, setSection] = useState('compensation');
  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-zinc-800/60" data-testid="emp-salary-subnav">
        {[
          { k: 'compensation', l: 'Compensation' },
          { k: 'payslips',     l: 'Payslips' },
          { k: 'leaves',       l: 'Leaves' },
          { k: 'rewards',      l: 'Rewards & Penalty' },
        ].map(s => (
          <button key={s.k} onClick={() => setSection(s.k)} data-testid={`emp-salary-sub-${s.k}`}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              section === s.k ? 'border-emerald-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}>
            {s.l}
          </button>
        ))}
      </div>
      {section === 'compensation' && <CompensationCard salary={salary} />}
      {section === 'payslips'     && <PayslipsCard payslips={payslips} />}
      {section === 'leaves'       && (
        <LeavesTab balance={balance} leaves={leaves} compOffLog={compOffLog} onApply={onApply} api={api} reload={reload} />
      )}
      {section === 'rewards'      && <RewardsPenaltyCard rewards={rewards} rules={rules} />}
    </div>
  );
}

// ──────────── Rewards & Penalty section ────────────
// Founder spec May 2026 — explains the deadline-incentive policy + shows the
// running monthly figure that will land on the artist's payslip.
function RewardsPenaltyCard({ rewards, rules }) {
  const fmtINR = (n) => `₹${Number(n || 0).toFixed(2)}`;
  const items = rewards?.items || [];
  return (
    <div className="space-y-4" data-testid="emp-rewards-tab">
      {/* Rules card — read this first */}
      <div className="bg-gradient-to-br from-amber-500/10 to-amber-700/5 border border-amber-500/40 rounded-md p-5">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-amber-300" />
          <h3 className="text-sm font-heading font-semibold text-white">How Rewards & Penalty Work — read me first</h3>
        </div>
        {rules ? (
          <ul className="space-y-1.5 text-xs text-zinc-200" data-testid="emp-rewards-rules">
            {(rules.rules || []).map((rule, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-300 mt-0.5">•</span>
                <span>{rule}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-zinc-400">Loading rules…</p>}
      </div>

      {/* Running monthly tally */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-4" data-testid="emp-rewards-total">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-emerald-300" />
            <p className="text-[10px] uppercase tracking-wider text-emerald-300">Rewards · This Month</p>
          </div>
          <p className="text-2xl font-heading font-bold text-emerald-200 mt-1">+{fmtINR(rewards?.rewards_total)}</p>
          <p className="text-[10px] text-zinc-400 mt-1">For tasks delivered &gt; 1 hour before deadline</p>
        </div>
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-md p-4" data-testid="emp-penalties-total">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-300" />
            <p className="text-[10px] uppercase tracking-wider text-rose-300">Penalty · This Month</p>
          </div>
          <p className="text-2xl font-heading font-bold text-rose-200 mt-1">−{fmtINR(rewards?.penalties_total)}</p>
          <p className="text-[10px] text-zinc-400 mt-1">For tasks delivered after the deadline</p>
        </div>
        <div className={`border rounded-md p-4 ${(rewards?.net_adjustment || 0) >= 0 ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-orange-500/10 border-orange-500/30'}`}
             data-testid="emp-rewards-net">
          <p className="text-[10px] uppercase tracking-wider text-cyan-300">Net Salary Adjustment</p>
          <p className={`text-2xl font-heading font-bold mt-1 ${(rewards?.net_adjustment || 0) >= 0 ? 'text-cyan-200' : 'text-orange-300'}`}>
            {(rewards?.net_adjustment || 0) >= 0 ? '+' : ''}{fmtINR(rewards?.net_adjustment)}
          </p>
          <p className="text-[10px] text-zinc-400 mt-1">Locks into payslip on month-end</p>
        </div>
      </div>

      {/* Per-task outcome list */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h4 className="text-xs font-semibold text-zinc-100 uppercase">Tasks Delivered This Month</h4>
          <span className="text-[10px] text-zinc-400">{items.length} task{items.length === 1 ? '' : 's'} · {rewards?.on_time_count || 0} on-time</span>
        </div>
        {items.length === 0 ? (
          <p className="text-xs text-zinc-500 text-center py-8">No completed tasks this month yet. Deliver early to earn rewards!</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-400 uppercase">
                <th className="text-left px-4 py-2">Task</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Outcome</th>
                <th className="text-right px-4 py-2">Δ Minutes</th>
                <th className="text-right px-4 py-2">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/40">
              {items.map(item => (
                <tr key={item.task_id} data-testid={`emp-reward-row-${item.task_id}`}>
                  <td className="px-4 py-2 text-xs text-zinc-100">
                    {item.title}
                    {item.parent_code && <span className="ml-2 font-mono text-[10px] text-zinc-500">{item.parent_code}</span>}
                  </td>
                  <td className="px-4 py-2 text-[10px] text-zinc-400 capitalize">{(item.task_type || '').replace(/_/g, ' ')}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                      item.kind === 'reward'  ? 'bg-emerald-500/20 text-emerald-300' :
                      item.kind === 'penalty' ? 'bg-rose-500/20 text-rose-300' :
                                                'bg-zinc-700/40 text-zinc-400'
                    }`}>{item.kind}</span>
                  </td>
                  <td className="px-4 py-2 text-xs font-mono text-right text-zinc-300">
                    {item.minutes > 0 ? '+' : ''}{item.minutes}
                  </td>
                  <td className={`px-4 py-2 text-xs font-mono font-semibold text-right ${
                    item.kind === 'reward' ? 'text-emerald-300' :
                    item.kind === 'penalty' ? 'text-rose-300' : 'text-zinc-400'
                  }`}>
                    {item.amount > 0 ? '+' : ''}{fmtINR(item.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ──────────── Leaves tab (now nested under Salary & Leaves) ────────────
function LeavesTab({ balance, leaves, compOffLog, onApply, api, reload }) {
  const cancelLeave = async (id) => {
    await api.post(`/employee/leaves/${id}/cancel`);
    reload();
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {balance ? Object.entries(balance).map(([kind, b]) => {
          const cls = LEAVE_CLASSES[kind] || LEAVE_CLASSES.loss_of_pay;
          // Earned leave is only granted via Comp-Off — surface that explicitly.
          const tooltip = kind === 'earned'
            ? 'Earned only via approved Comp-Off (Sunday/holiday work). See log below.'
            : `Auto-accrual: ${b.annual_quota / 12 || 0}/month, capped at ${b.annual_quota}/year`;
          // For earned, the "quota" bar is meaningless — show a flat indicator
          // instead of dividing by zero.
          const denom = b.annual_quota > 0 ? b.annual_quota : Math.max(b.accrued, 1);
          return (
            <div key={kind} data-testid={`emp-leave-balance-${kind}`}
              className={`bg-zinc-900 border rounded-md p-4 ${cls.card}`} title={tooltip}>
              <p className={`text-[10px] uppercase tracking-wider ${cls.label}`}>{LEAVE_KIND_LABELS[kind] || kind}</p>
              <p className="text-2xl font-heading font-bold text-white mt-1">{b.balance}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">
                accrued {b.accrued} · used {b.used}
                {b.annual_quota > 0 ? <span className="text-zinc-500"> / quota {b.annual_quota}</span>
                                    : <span className="text-zinc-500"> · comp-off only</span>}
              </p>
              <div className="mt-2 h-1 bg-zinc-800 rounded-full overflow-hidden">
                <div className={`h-full ${cls.bar}`} style={{ width: `${Math.min(100, (b.used / denom) * 100)}%` }} />
              </div>
            </div>
          );
        }) : <p className="text-xs text-zinc-400 col-span-4">Loading balance...</p>}
        <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-700/5 border border-cyan-500/40 rounded-md p-4 flex flex-col items-center justify-center gap-2">
          <CalendarDays className="w-6 h-6 text-cyan-300" />
          <button onClick={onApply} data-testid="emp-apply-leave-btn"
            className="h-8 px-3 bg-cyan-500 hover:bg-cyan-400 rounded-md text-white text-xs font-medium flex items-center gap-1.5">
            <Plus className="w-3 h-3" /> Apply for Leave
          </button>
        </div>
      </div>

      {/* Comp-Off log — answers "where did this earned leave come from?" */}
      {Array.isArray(compOffLog) && compOffLog.length > 0 && (
        <div className="bg-zinc-900 border border-emerald-500/20 rounded-md overflow-hidden" data-testid="emp-comp-off-log">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-emerald-200 uppercase">Earned Leave Log</h3>
            <span className="text-[10px] text-emerald-400">{compOffLog.length} grant{compOffLog.length === 1 ? '' : 's'}</span>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-400 uppercase">
                <th className="text-left px-4 py-2">Work Date</th>
                <th className="text-left px-4 py-2">+Days</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Granted On</th>
                <th className="text-left px-4 py-2">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/40">
              {compOffLog.map(g => (
                <tr key={g.id}>
                  <td className="px-4 py-2 text-xs font-mono text-zinc-200">{g.work_date}</td>
                  <td className="px-4 py-2 text-xs font-mono text-emerald-300">+{g.days}</td>
                  <td className="px-4 py-2 text-xs text-zinc-300">{g.reason}</td>
                  <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">{(g.granted_at || '').slice(0, 10)}</td>
                  <td className="px-4 py-2 text-[10px] text-zinc-400">{g.granted_by_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h3 className="text-xs font-semibold text-zinc-100 uppercase">My Leave Requests</h3>
        </div>
        {leaves.length === 0 ? (
          <p className="text-xs text-zinc-400 text-center py-8">No leave requests yet</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-400 uppercase">
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">From</th>
                <th className="text-left px-4 py-2">To</th>
                <th className="text-left px-4 py-2">Days</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/40" data-testid="emp-leave-requests-list">
              {leaves.map(l => (
                <tr key={l.id}>
                  <td className="px-4 py-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] capitalize ${(LEAVE_CLASSES[l.kind] || LEAVE_CLASSES.loss_of_pay).pill}`}>
                      {LEAVE_KIND_LABELS[l.kind] || l.kind}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-zinc-300 font-mono">{l.start_date}</td>
                  <td className="px-4 py-2 text-xs text-zinc-300 font-mono">{l.end_date}</td>
                  <td className="px-4 py-2 text-xs text-zinc-100 font-mono">{l.days}</td>
                  <td className="px-4 py-2 text-xs text-zinc-400 truncate max-w-[20rem]">{l.reason || '—'}</td>
                  <td className="px-4 py-2 text-xs">
                    <StatusPill status={l.status} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    {['pending', 'approved'].includes(l.status) && (
                      <button onClick={() => cancelLeave(l.id)} data-testid={`emp-leave-cancel-${l.id}`}
                        className="text-[10px] text-rose-300 hover:text-rose-100">Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ──────────── Apply Leave Modal ────────────
function ApplyLeaveModal({ api, onClose, onCreated }) {
  const [form, setForm] = useState({ kind: 'casual', start_date: '', end_date: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/employee/leaves/apply', form);
      onCreated();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 w-[28rem]" onClick={e => e.stopPropagation()} data-testid="emp-apply-leave-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-heading font-semibold text-white flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-cyan-400" /> Apply for Leave
          </h3>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-zinc-300 uppercase tracking-wider">Kind</label>
            <div className="flex gap-1.5 mt-1">
              {Object.entries(LEAVE_KIND_LABELS).map(([k, l]) => (
                <button key={k} onClick={() => setForm(f => ({ ...f, kind: k }))} data-testid={`emp-leave-kind-${k}`}
                  className={`text-[11px] px-3 py-1.5 rounded border ${form.kind === k ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-200' : 'bg-zinc-950 border-zinc-800 text-zinc-300'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-zinc-300 uppercase tracking-wider">From</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                data-testid="emp-leave-start"
                className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-white focus:outline-none focus:border-cyan-500/50" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-300 uppercase tracking-wider">To</label>
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                data-testid="emp-leave-end"
                className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-white focus:outline-none focus:border-cyan-500/50" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-zinc-300 uppercase tracking-wider">Reason</label>
            <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={3}
              data-testid="emp-leave-reason"
              className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-white focus:outline-none focus:border-cyan-500/50" />
          </div>
          {err && (
            <div className="p-2 bg-rose-500/10 border border-rose-500/30 rounded text-[11px] text-rose-300 flex items-start gap-2" data-testid="emp-leave-err">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {err}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-xs px-3 py-1.5 text-zinc-300 hover:text-white">Cancel</button>
          <button onClick={submit} disabled={busy || !form.start_date} data-testid="emp-leave-submit"
            className="text-xs px-4 py-1.5 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-30 rounded text-white font-medium flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────── Compensation card (split out from old SalaryTab) ────────────
function CompensationCard({ salary }) {
  const ctc = salary?.current_ctc || {};
  const fmtINR = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
  const hasCTC = !!ctc.annual;
  return (
    <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-700/5 border border-emerald-500/30 rounded-md p-5">
      <div className="flex items-center gap-3 mb-3">
        <Wallet className="w-5 h-5 text-emerald-300" />
        <h3 className="text-sm font-heading font-semibold text-white">My Compensation</h3>
      </div>
      {hasCTC ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SalCard label="Annual CTC" v={fmtINR(ctc.annual)} highlight />
          <SalCard label="Gross / Month" v={fmtINR(ctc.gross_monthly)} />
          <SalCard label="Net / Month" v={fmtINR(ctc.net_monthly)} highlight />
          <SalCard label="TDS / Month" v={fmtINR(ctc.tds_monthly)} />
          <SalCard label="Basic" v={fmtINR(ctc.basic)} />
          <SalCard label="HRA" v={fmtINR(ctc.hra)} />
          <SalCard label="Special Allow." v={fmtINR(ctc.special_allowance)} />
          <SalCard label="PF (Employee)" v={fmtINR(ctc.pf_employee)} />
        </div>
      ) : (
        <p className="text-xs text-zinc-400">CTC breakdown not yet configured. Please contact HR.</p>
      )}
    </div>
  );
}

// ──────────── Payslips card (split out from old SalaryTab) ────────────
function PayslipsCard({ payslips }) {
  const fmtINR = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="text-xs font-semibold text-zinc-100 uppercase">Payslips</h3>
        <span className="text-[10px] text-zinc-400">{payslips.length} on file</span>
      </div>
      {payslips.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <FileText className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-xs text-zinc-400">No payslips generated yet. HR will release them monthly.</p>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800 text-[10px] text-zinc-400 uppercase">
              <th className="text-left px-4 py-2">Period</th>
              <th className="text-left px-4 py-2">Paid Days</th>
              <th className="text-left px-4 py-2">LOP</th>
              <th className="text-right px-4 py-2">Gross</th>
              <th className="text-right px-4 py-2">Deductions</th>
              <th className="text-right px-4 py-2">Net Pay</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/40" data-testid="emp-payslips-list">
            {payslips.map(p => {
              const rewardsLine = Number(p.earnings?.rewards || 0);
              const penaltiesLine = Number(p.deductions?.penalties || 0);
              return (
                <tr key={p.id}>
                  <td className="px-4 py-2 text-xs text-zinc-100">
                    {p.month_label}
                    {(rewardsLine || penaltiesLine) ? (
                      <div className="flex gap-2 mt-0.5">
                        {rewardsLine > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">
                            <Trophy className="w-2.5 h-2.5 inline mr-0.5" />
                            +₹{rewardsLine.toFixed(2)} reward
                          </span>
                        )}
                        {penaltiesLine > 0 && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300">
                            <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />
                            −₹{penaltiesLine.toFixed(2)} penalty
                          </span>
                        )}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-xs text-zinc-200 font-mono">{p.paid_days}/{p.total_workdays}</td>
                  <td className="px-4 py-2 text-xs font-mono text-rose-300">{p.lop_days}</td>
                  <td className="px-4 py-2 text-xs text-zinc-100 font-mono text-right">{fmtINR(p.earnings?.gross)}</td>
                  <td className="px-4 py-2 text-xs text-rose-300 font-mono text-right">-{fmtINR(p.deductions?.total)}</td>
                  <td className="px-4 py-2 text-xs text-emerald-300 font-mono font-semibold text-right">{fmtINR(p.net_pay)}</td>
                  <td className="px-4 py-2 text-right">
                    <button className="text-[10px] text-cyan-300 hover:text-cyan-100 flex items-center gap-1 ml-auto opacity-50 cursor-not-allowed" disabled>
                      <Download className="w-3 h-3" /> PDF
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ──────────── atoms ────────────
const Stat = ({ label, value, color }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 text-center">
    <p className={`text-xl font-heading font-bold ${color}`}>{value}</p>
    <p className="text-[10px] text-zinc-400">{label}</p>
  </div>
);

const Column = ({ title, accent = 'border-zinc-800', children }) => (
  <div className={`bg-zinc-900 border ${accent} rounded-md`}>
    <div className="px-4 py-3 border-b border-zinc-800">
      <h3 className="text-xs font-semibold text-zinc-100 uppercase">{title}</h3>
    </div>
    <div className="divide-y divide-zinc-800/40 max-h-[40vh] overflow-y-auto">{children}</div>
  </div>
);

const Empty = ({ children }) => <p className="text-xs text-zinc-400 text-center py-6">{children}</p>;

const TaskRow = ({ t, updating, updateTask }) => (
  <div className="px-4 py-3">
    <p className="text-xs text-zinc-100 font-medium">{t.name}</p>
    <div className="flex items-center gap-2 mt-1">
      <span className="text-[9px] text-zinc-400">{t.task_type}</span>
      <span className="text-[9px] font-mono text-zinc-500">Due: {t.due_date ? new Date(t.due_date).toLocaleDateString() : '—'}</span>
    </div>
    <div className="flex items-center gap-2 mt-2">
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${t.progress}%` }} />
      </div>
      <span className="text-[9px] font-mono text-zinc-400">{t.progress}%</span>
    </div>
    <div className="flex gap-1.5 mt-2">
      {/* Submit / Done bypass buttons removed May 24, 2026 (founder spec):
          status advances ONLY through the inline SUBMIT (CBA Clip / Shot)
          flow → 3-gate pipeline. The TaskRow visual component is still
          rendered (for the active-task callout strip) but the actions live
          on the dedicated MY TASKS row above. */}
    </div>
  </div>
);

const SalCard = ({ label, v, highlight }) => (
  <div className={`rounded-md p-3 border ${highlight ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-zinc-950 border-zinc-800'}`}>
    <p className="text-[10px] text-zinc-300 uppercase tracking-wider">{label}</p>
    <p className={`mt-1 text-base font-mono ${highlight ? 'text-emerald-200 font-semibold' : 'text-white'}`}>{v}</p>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    cancelled: 'bg-zinc-700/40 text-zinc-300 border-zinc-700',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border capitalize ${map[status] || 'bg-zinc-800 text-zinc-300'}`}>
      {status}
    </span>
  );
};
